import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator
} from './ui/command';
import {
  LayoutDashboard, Inbox, FileText, Shield, ShieldCheck, BarChart3,
  Globe, AlertTriangle, Brain, Printer, Users, Settings, Plug, Eye,
  Building2, FileWarning, HeartPulse, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ALL_ROUTES = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, group: 'Navegação' },
  { name: 'Meus Atos', href: '/inbox', icon: Inbox, group: 'Navegação' },
  { name: 'Atos de Controle', href: '/acts', icon: FileText, group: 'Navegação' },
  { name: 'Novo Ato', href: '/acts/new', icon: FileText, group: 'Ações', requiresCreate: true },
  { name: 'TCE', href: '/tce', icon: FileWarning, group: 'Navegação' },
  { name: 'Integridade', href: '/integridade', icon: Shield, group: 'Compliance' },
  { name: 'LGPD', href: '/lgpd', icon: ShieldCheck, group: 'Compliance' },
  { name: 'Governança', href: '/governanca', icon: BarChart3, group: 'Compliance' },
  { name: 'Transparência', href: '/transparencia', icon: Globe, group: 'Compliance' },
  { name: 'Alertas', href: '/alerts', icon: AlertTriangle, group: 'Compliance' },
  { name: 'AI Engine', href: '/ai-engine', icon: Brain, group: 'Inteligência' },
  { name: 'Relatórios', href: '/relatorios', icon: Printer, group: 'Inteligência' },
  { name: 'Auditoria', href: '/audit', icon: FileText, group: 'Inteligência' },
  { name: 'Casa Civil', href: '/dashboard/casa-civil', icon: Building2, group: 'Painéis' },
  { name: 'CGE', href: '/dashboard/cge', icon: Eye, group: 'Painéis' },
  { name: 'Health / Status', href: '/health', icon: HeartPulse, group: 'Sistema' },
  { name: 'Central de Ajuda', href: '/help', icon: HelpCircle, group: 'Sistema' },
  { name: 'Integrações', href: '/integrations', icon: Plug, group: 'Admin' },
  { name: 'Usuários', href: '/users', icon: Users, group: 'Admin' },
  { name: 'Configurações', href: '/settings', icon: Settings, group: 'Admin' },
];

export const GlobalSearch = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { api, canCreateAct } = useAuth();
  const [acts, setActs] = useState([]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    api.get('/acts', { params: { limit: 25 } })
      .then(r => { if (active) setActs(r.data.items || r.data || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, api]);

  useEffect(() => {
    const down = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const routes = ALL_ROUTES.filter(r => !r.requiresCreate || canCreateAct());
  const grouped = routes.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Buscar atos, módulos, ações..." data-testid="global-search-input" />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>

        {acts.length > 0 && (
          <>
            <CommandGroup heading="Atos Recentes">
              {acts.slice(0, 8).map((a) => (
                <CommandItem
                  key={a.act_id}
                  value={`${a.act_number || ''} ${a.scope || ''} ${a.description || ''} ${a.control_body || ''}`}
                  onSelect={() => go(`/acts/${a.act_id}`)}
                  data-testid={`search-act-${a.act_id}`}
                >
                  <FileText className="w-4 h-4 mr-2 text-slate-500" />
                  <div className="flex flex-col">
                    <span className="text-sm">{a.act_number || a.act_id.substring(0, 12)}</span>
                    <span className="text-xs text-slate-500 truncate max-w-md">{a.scope || a.description}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {Object.entries(grouped).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((r) => (
              <CommandItem
                key={r.href}
                value={r.name}
                onSelect={() => go(r.href)}
                data-testid={`search-route-${r.href.replace(/\//g, '-')}`}
              >
                <r.icon className="w-4 h-4 mr-2 text-slate-500" />
                {r.name}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
